import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import copy from 'copy-to-clipboard';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from '../../../hooks/useToast';
import { DEFAULT_GRID_ROWS } from '../../PageConfigProvider/constants';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type {
  Position,
  PositionedItem,
} from '../../PageConfigProvider/types/item';
import type { ChartClipboardContextMenuState } from '../components/ChartClipboardContextMenu';
import { useGrid } from '../GridProvider';
import {
  type ChartClipboardConfig,
  parseChartClipboardConfig,
  serializeChartClipboardConfig,
} from '../utils/chart-clipboard';
import {
  resolveDropPlacement,
  validatePosition,
} from '../utils/grid-layout-utils';
import {
  clampPositionToReportPage,
  getReportInnerBoundsForY,
} from '../utils/report-page-boundaries';

interface UseEditableGridOptions {
  onBuilderOpenChange?: (isOpen: boolean) => void;
  isReport?: boolean;
}

interface UseEditableGridReturn {
  openConfigId: string | null;
  closeConfig: () => void;
  selectedIds: Set<string>;
  clearSelection: () => void;
  handleChartClick: (id: string, event: React.MouseEvent) => void;
  filteredGridItems: PositionedItem[];
  selectedItem: PositionedItem | undefined;
  handleAddItem: (position: Position) => void;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  clipboardContextMenu: ChartClipboardContextMenuState | null;
  closeClipboardContextMenu: () => void;
  handleClipboardContextMenuAction: (action: 'copy' | 'cut' | 'paste') => void;
  handleChartContextMenu: (
    item: PositionedItem,
    event: React.MouseEvent
  ) => void;
  handleGhostContextMenu: (position: Position, event: React.MouseEvent) => void;
  handleGhostMouseEnter: (position: Position) => void;
  handleGhostMouseLeave: (position: Position) => void;
}

const isEditableClipboardTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement ||
  target instanceof HTMLTextAreaElement ||
  (target instanceof HTMLElement && target.isContentEditable);

const positionsMatch = (a: Position, b: Position): boolean =>
  a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;

type PasteDestination = {
  position?: Position;
  overwriteItemId?: string;
};

export const useEditableGrid = ({
  onBuilderOpenChange,
  isReport = false,
}: UseEditableGridOptions): UseEditableGridReturn => {
  const {
    gridItems,
    grid,
    itemDragging,
    setItemDragging,
    updateItemPosition,
    updateItemPositions,
  } = useGrid();
  const { dispatch } = useTabContentConfig();
  const toast = useToast();

  const [openConfigId, setOpenConfigId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [clipboardContextMenu, setClipboardContextMenu] =
    useState<ChartClipboardContextMenuState | null>(null);
  const gridItemsRef = useRef(gridItems);
  const selectedIdsRef = useRef(selectedIds);
  const hoveredGhostPositionRef = useRef<Position | null>(null);
  const overwriteTargetIdRef = useRef<string | null>(null);
  const lastClipboardTextRef = useRef<string | null>(null);
  const isProgrammaticClipboardWriteRef = useRef(false);
  gridItemsRef.current = gridItems;
  selectedIdsRef.current = selectedIds;

  const selectedItem = gridItems.find((item) => item.id === openConfigId);

  const openConfig = useCallback(
    (id: string) => {
      setOpenConfigId(id);
      setSelectedIds(new Set([id]));
      onBuilderOpenChange?.(true);
    },
    [onBuilderOpenChange]
  );

  const closeConfig = useCallback(() => {
    setOpenConfigId(null);
    onBuilderOpenChange?.(false);
  }, [onBuilderOpenChange]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    overwriteTargetIdRef.current = null;
  }, []);

  const handleChartClick = (id: string, event: React.MouseEvent) => {
    if (event.shiftKey) {
      overwriteTargetIdRef.current = null;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    } else {
      overwriteTargetIdRef.current = id;
      setSelectedIds(new Set([id]));
      setOpenConfigId(id);
      onBuilderOpenChange?.(true);
    }
  };

  useEffect(() => {
    if (openConfigId === null) return;
    const stillExists = gridItems.some((item) => item.id === openConfigId);
    if (!stillExists) {
      setOpenConfigId(null);
      overwriteTargetIdRef.current = null;
      onBuilderOpenChange?.(false);
    }
  }, [gridItems, openConfigId, onBuilderOpenChange]);

  const closeClipboardContextMenu = useCallback(() => {
    setClipboardContextMenu(null);
  }, []);

  const writeChartToClipboard = useCallback(
    (item: PositionedItem, clipboardData?: DataTransfer | null): boolean => {
      const text = serializeChartClipboardConfig(item);
      const didWrite = (() => {
        if (clipboardData) {
          clipboardData.setData('text/plain', text);
          return true;
        }

        isProgrammaticClipboardWriteRef.current = true;
        try {
          return copy(text);
        } finally {
          isProgrammaticClipboardWriteRef.current = false;
        }
      })();

      if (didWrite) {
        lastClipboardTextRef.current = text;
        overwriteTargetIdRef.current = null;
      }
      return didWrite;
    },
    []
  );

  const removeCutItem = useCallback(
    (id: string) => {
      dispatch({ type: 'DELETE_ITEM', payload: { id } });
      setSelectedIds(new Set());
      overwriteTargetIdRef.current = null;
      closeConfig();
    },
    [closeConfig, dispatch]
  );

  const pasteChartConfig = useCallback(
    (chart: ChartClipboardConfig, destination: PasteDestination = {}) => {
      const currentItems = gridItemsRef.current;
      const hoveredPosition =
        destination.position ?? hoveredGhostPositionRef.current;
      const explicitOverwriteId = destination.overwriteItemId;
      const selectedOverwriteId =
        selectedIdsRef.current.size === 1 ? overwriteTargetIdRef.current : null;
      const overwriteItemId =
        hoveredPosition === null || hoveredPosition === undefined
          ? (explicitOverwriteId ?? selectedOverwriteId)
          : null;

      if (overwriteItemId) {
        const target = currentItems.find((item) => item.id === overwriteItemId);
        if (target) {
          const overwrittenItem: PositionedItem = {
            ...chart,
            id: target.id,
            position: target.position,
            ...(target.slotId !== undefined && { slotId: target.slotId }),
          };
          dispatch({
            type: 'SET_ITEMS',
            payload: currentItems.map((item) =>
              item.id === target.id ? overwrittenItem : item
            ),
          });
          openConfig(target.id);
          overwriteTargetIdRef.current = target.id;
          return;
        }
      }

      const requestedPosition = hoveredPosition ?? chart.position;
      const reportContentStartY = Math.min(DEFAULT_GRID_ROWS, grid.rows - 1);
      const reportContentEndY = Math.max(
        reportContentStartY,
        grid.rows - DEFAULT_GRID_ROWS - 1
      );
      const reportBaselineY = isReport
        ? Math.max(
            reportContentStartY,
            Math.min(requestedPosition.y, reportContentEndY)
          )
        : Math.max(0, Math.min(requestedPosition.y, grid.rows - 1));
      const desiredPosition = isReport
        ? clampPositionToReportPage(requestedPosition, reportBaselineY)
        : requestedPosition;
      const reportBounds = isReport
        ? getReportInnerBoundsForY(reportBaselineY)
        : null;

      const canUseHoveredPosition =
        hoveredPosition !== null &&
        hoveredPosition !== undefined &&
        validatePosition(grid, desiredPosition, currentItems);
      const placement = canUseHoveredPosition
        ? { position: desiredPosition }
        : resolveDropPlacement({
            grid,
            desired: desiredPosition,
            items: currentItems,
            maxNearestOffset: {
              columns: Math.floor(grid.columns / 2),
              rows: Math.floor(grid.rows / 2),
            },
            ...(hoveredPosition && {
              dropPoint: {
                x: desiredPosition.x,
                y: desiredPosition.y,
              },
            }),
            ...(reportBounds && {
              yBounds: {
                yStart: reportBounds.innerStart,
                yEnd: reportBounds.innerEnd,
              },
            }),
          });

      if (!placement) {
        toast({
          title: 'Chart could not be pasted',
          description: 'There is not enough empty space on this page.',
          status: 'warning',
        });
        return;
      }

      const id = crypto.randomUUID();
      const pastedItem: PositionedItem = {
        ...chart,
        id,
        position: placement.position,
      };
      dispatch({ type: 'SET_ITEMS', payload: [...currentItems, pastedItem] });
      openConfig(id);
      overwriteTargetIdRef.current = null;
    },
    [dispatch, grid, isReport, openConfig, toast]
  );

  const pasteClipboardText = useCallback(
    (text: string, destination?: PasteDestination): boolean => {
      const chart = parseChartClipboardConfig(text);
      if (!chart) return false;
      lastClipboardTextRef.current = text;
      pasteChartConfig(chart, destination);
      return true;
    },
    [pasteChartConfig]
  );

  useEffect(() => {
    const handleCopy = (event: ClipboardEvent) => {
      if (isProgrammaticClipboardWriteRef.current) return;
      if (isEditableClipboardTarget(event.target)) return;
      if (selectedIdsRef.current.size !== 1) return;
      const selectedId = selectedIdsRef.current.values().next().value;
      const item = gridItemsRef.current.find(
        (gridItem) => gridItem.id === selectedId
      );
      if (!item || !writeChartToClipboard(item, event.clipboardData)) return;
      event.preventDefault();
    };

    const handleCut = (event: ClipboardEvent) => {
      if (isEditableClipboardTarget(event.target)) return;
      if (selectedIdsRef.current.size !== 1) return;
      const selectedId = selectedIdsRef.current.values().next().value;
      const item = gridItemsRef.current.find(
        (gridItem) => gridItem.id === selectedId
      );
      if (!item || !writeChartToClipboard(item, event.clipboardData)) return;
      event.preventDefault();
      removeCutItem(item.id);
    };

    const handlePaste = (event: ClipboardEvent) => {
      if (isEditableClipboardTarget(event.target)) return;
      const text = event.clipboardData?.getData('text/plain') ?? '';
      if (!pasteClipboardText(text)) return;
      event.preventDefault();
    };

    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('paste', handlePaste);
    };
  }, [pasteClipboardText, removeCutItem, writeChartToClipboard]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (isEditableClipboardTarget(event.target)) return;
      if (selectedIds.size === 0) return;
      event.preventDefault();
      dispatch({
        type: 'DELETE_ITEMS',
        payload: { ids: Array.from(selectedIds) },
      });
      setSelectedIds(new Set());
      overwriteTargetIdRef.current = null;
      closeConfig();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeConfig, selectedIds, dispatch]);

  const handleAddItem = (position: Position) => {
    const id = crypto.randomUUID();
    dispatch({ type: 'ADD_BLANK_ITEM', payload: { position, id } });
    openConfig(id);
    overwriteTargetIdRef.current = null;
  };

  const handleChartContextMenu = (
    item: PositionedItem,
    event: React.MouseEvent
  ) => {
    event.preventDefault();
    event.stopPropagation();
    openConfig(item.id);
    overwriteTargetIdRef.current = item.id;
    setClipboardContextMenu({
      x: event.clientX,
      y: event.clientY,
      target: { type: 'chart', itemId: item.id },
    });
  };

  const handleGhostContextMenu = (
    position: Position,
    event: React.MouseEvent
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setClipboardContextMenu({
      x: event.clientX,
      y: event.clientY,
      target: { type: 'ghost', position },
    });
  };

  const handleGhostMouseEnter = (position: Position) => {
    hoveredGhostPositionRef.current = position;
  };

  const handleGhostMouseLeave = (position: Position) => {
    if (
      hoveredGhostPositionRef.current &&
      positionsMatch(hoveredGhostPositionRef.current, position)
    ) {
      hoveredGhostPositionRef.current = null;
    }
  };

  const handleClipboardContextMenuAction = (
    action: 'copy' | 'cut' | 'paste'
  ) => {
    const menu = clipboardContextMenu;
    closeClipboardContextMenu();
    if (!menu) return;

    if (action === 'copy' || action === 'cut') {
      if (menu.target.type !== 'chart') return;
      const { itemId } = menu.target;
      const item = gridItemsRef.current.find(
        (gridItem) => gridItem.id === itemId
      );
      if (!item) return;
      if (!writeChartToClipboard(item)) {
        toast({
          title: `Chart could not be ${action === 'copy' ? 'copied' : 'cut'}`,
          status: 'error',
        });
        return;
      }
      if (action === 'cut') removeCutItem(item.id);
      return;
    }

    const destination: PasteDestination =
      menu.target.type === 'ghost'
        ? { position: menu.target.position }
        : { overwriteItemId: menu.target.itemId };

    void (async () => {
      let clipboardText = '';
      try {
        clipboardText = (await navigator.clipboard?.readText()) ?? '';
      } catch {
        clipboardText = lastClipboardTextRef.current ?? '';
      }
      if (!clipboardText) {
        clipboardText = lastClipboardTextRef.current ?? '';
      }
      if (pasteClipboardText(clipboardText, destination)) return;
    })();
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeItem = active?.data?.current as PositionedItem | undefined;
    if (!activeItem) return;
    setItemDragging(activeItem);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const activeItem = itemDragging;
    if (!activeItem) return;
    const activationCoordinates = getEventCoordinates(event.activatorEvent);
    const pointerCoordinates = activationCoordinates
      ? {
          x: activationCoordinates.x + event.delta.x,
          y: activationCoordinates.y + event.delta.y,
        }
      : null;

    if (selectedIds.has(activeItem.id) && selectedIds.size > 1) {
      const itemsToMove = gridItems.filter((item) => selectedIds.has(item.id));
      if (itemsToMove.length > 0) {
        updateItemPositions({ items: itemsToMove, delta: event.delta });
      }
    } else {
      updateItemPosition({
        item: activeItem,
        delta: event.delta,
        pointerCoordinates,
      });
    }

    setItemDragging(null);
  };

  return {
    openConfigId,
    closeConfig,
    selectedIds,
    clearSelection,
    handleChartClick,
    filteredGridItems: gridItems,
    selectedItem,
    handleAddItem,
    handleDragStart,
    handleDragEnd,
    clipboardContextMenu,
    closeClipboardContextMenu,
    handleClipboardContextMenuAction,
    handleChartContextMenu,
    handleGhostContextMenu,
    handleGhostMouseEnter,
    handleGhostMouseLeave,
  };
};
